import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { PillRadio, CustomInput } from "../../components-new/common";
import { API_BASE_PATH, API_BASE_RELATIVE_PATH } from "../../Config/Config";
import {
  useUpdateIntegrationSettings,
  useStaticIntegrationsQuery,
  useDeleteTestSuiteIntegration,
  useIntegrationsByGroupQuery,
} from "../../useQuery";
import { useNotification } from "../../hooks";
import { useQueryClient } from "@tanstack/react-query";
import apiFetch from "../../utils/apiFetch";

const StyledLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "13px",
  fontWeight: 500,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: "15px 28px",
  borderRadius: "8px",
  textTransform: "capitalize",
  textAlign: "center",
  fontSize: "17px",
  fontWeight: 600,
  lineHeight: "20px",
  cursor: "pointer",
}));

const integrationTypeMap = {
  "Azure Dev Ops": "1",
  AzureDevOps: "1",
  "MS Teams Channel": "2",
  MSTeamsChannel: "2",
  Jira: "3",
  Slack: "4",
  API_Github: "6",
  APIGithub: "6",
  Github: "7",
  "Impact Analysis": "8",
  ImpactAnalysis: "8",
  "Service Now": "9",
  ServiceNow: "9",
};

const reverseIntMap = {
  1: "AzureDevOps",
  2: "MSTeamsChannel",
  3: "Jira",
  4: "Slack",
  5: "ServiceNow",
  6: "APIGithub",
  7: "Github",
  8: "ImpactAnalysis",
  9: "ServiceNow",
};

//TODO: will need to refactor this component.

const IntegrationTab = forwardRef(({ suite, licenceKey }, ref) => {
  const notify = useNotification();
  const queryClient = useQueryClient();
  const { data: staticIntegrations } = useStaticIntegrationsQuery();

  const { mutate: saveIntegrationSettings } = useUpdateIntegrationSettings({
    onSuccess: () => {
      notify("Integration settings saved", "success");
      queryClient.invalidateQueries({ queryKey: ["testSuiteNew"], exact: false });
      queryClient.refetchQueries({ queryKey: ["testSuiteNew"], exact: false });
    },
    onError: () => {
      notify("Failed to save integration settings", "error");
    },
  });

  const { mutate: removeIntegration } = useDeleteTestSuiteIntegration({
    onSuccess: () => {
      setSelectedIntegration("");
      setIssueobj({ logType: "", logId: "", projectkey: "", message: "" });
      setServiceNowData({ category: "", subcategory: "", issueTitle: "", message: "" });
      setWorkItemIds({
        "Azure Dev Ops": "",
        Jira: "",
        "MS Teams Channel": "",
        Slack: "",
        ServiceNow: "",
      });
      setLogLevelToggle(false);
      setResultTypeToggle(false);
      notify("Integration removed", "success");
      queryClient.invalidateQueries({ queryKey: ["testSuiteNew"], exact: false });
      queryClient.refetchQueries({ queryKey: ["testSuiteNew"], exact: false });
    },
    onError: () => {
      notify("Failed to remove integration", "error");
    },
  });

  const userDetails = localStorage.getItem("userDetail");
  if (!userDetails) throw new Error("No user details found in localStorage");
  const temp = JSON.parse(userDetails);

  const [selectedIntegration, setSelectedIntegration] = useState("");
  const [integrationOptions, setIntegrationOptions] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState(true);
  const [issueobj, setIssueobj] = useState({
    logType: "",
    logId: "",
    projectkey: "",
    message: "",
  });
  const [serviceNowData, setServiceNowData] = useState({
    category: "",
    subcategory: "",
    issueTitle: "",
    message: "",
  });
  const [logLevelToggle, setLogLevelToggle] = useState(false);
  const [resultTypeToggle, setResultTypeToggle] = useState(false);
  const [workItemIds, setWorkItemIds] = useState({
    "Azure Dev Ops": "",
    Jira: "",
    "MS Teams Channel": "",
    Slack: "",
    ServiceNow: "",
  });
  const [exceUrl, setExceUrl] = useState("");
  const [aioTestCycleId, setAioTestCycleId] = useState(""); 
  const [serviceNowDropdownConfig, setServiceNowDropdownConfig] = useState(null);

  const { data: issueMgmtData } = useIntegrationsByGroupQuery(
    "defect management",
    suite?.productid,
  );
  const { data: communicationData } = useIntegrationsByGroupQuery(
    "communication",
    suite?.productid,
  );

  const issueMgmtOptions = (issueMgmtData?.integrations || []).map((item) => ({
    value: item.name,
    label: item.name,
    staticintid: String(item.id),
  }));

  const communicationOptions = (communicationData?.integrations || []).map((item) => ({
    value: item.name,
    label: item.name,
    staticintid: String(item.id),
  }));

  // Derive intType (staticintid is the actual integration ID the backend expects)
  let intType = null;

  if (selectedIntegration) {
    const matchedOption = integrationOptions.find(
      (opt) =>
        opt.value === selectedIntegration || opt.value.replace(/\s+/g, "") === selectedIntegration,
    );
    intType = matchedOption?.staticintid || integrationTypeMap[selectedIntegration] || null;
  }

  // Expose integration data to parent (for handleRun)
  useImperativeHandle(ref, () => ({
    getIntegrationData: () => ({
      selectedIntegration,
      intType,
      issueobj,
      serviceNowData,
      workItemIds,
      logLevelToggle,
      resultTypeToggle,
      aioTestCycleId,
    }),
  }));

  useEffect(() => {
    const fetchIntegrations = async () => {
      try {
        setIsLoadingIntegrations(true);
        const companyid = temp.companyid;
        const token = temp.token;

        if (!companyid || !token) {
          setIsLoadingIntegrations(false);
          return;
        }

        const response = await apiFetch(
          `${API_BASE_PATH}${API_BASE_RELATIVE_PATH}?action=getStaticIntegrations&companyid=${companyid}`,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch integrations");
        }

        const responseData = await response.json();
        const mappedIntegrations = responseData.map((integration) => ({
          value: integration.intname || integration.name || integration.value,
          label: integration.intname || integration.label || integration.name,
          staticintid: String(integration.staticintid || integration.integrationid || ""),
        }));

        const serviceNowEntry = responseData.find(
          (item) => item.intname === "Service Now" || item.intname === "ServiceNow",
        );
        if (serviceNowEntry?.dropdown_config) {
          try {
            const parsed =
              typeof serviceNowEntry.dropdown_config === "string"
                ? JSON.parse(serviceNowEntry.dropdown_config)
                : serviceNowEntry.dropdown_config;
            setServiceNowDropdownConfig(parsed);
          } catch (e) {
            console.error("Failed to parse ServiceNow dropdown_config", e);
          }
        }

        setIntegrationOptions(mappedIntegrations);
      } catch (error) {
        notify("Failed to load integrations", "error");
        setIntegrationOptions([
          { value: "Jira", label: "Jira" },
          { value: "ServiceNow", label: "ServiceNow" },
          { value: "Azure Dev Ops", label: "Azure DevOps" },
          { value: "Slack", label: "Slack" },
          { value: "MS Teams Channel", label: "MS Teams" },
        ]);
      } finally {
        setIsLoadingIntegrations(false);
      }
    };

    if (temp.companyid && licenceKey) {
      fetchIntegrations();
    }
  }, [temp.companyid, temp.token]);

  useEffect(() => {
    if (!suite?.integrations?.intid) return;

    const intName = reverseIntMap[String(suite.integrations.intid)];
    if (intName) {
      setSelectedIntegration(intName);
    }

    setLogLevelToggle(suite.integrations.execLogLevel === "CaseLevel");
    setResultTypeToggle(suite.integrations.execLogType === "Both");
    setAioTestCycleId(suite.integrations.aioTestCycleId || "");

    if (intName === "Jira" || intName === "AzureDevOps") {
      setIssueobj({
        logType: suite.integrations.logType || "",
        logId: suite.integrations.logId || "",
        projectkey: suite.integrations.projectKey || "",
        message: suite.integrations.issueDescription || "",
      });
    } else if (intName === "ServiceNow") {
      setServiceNowData({
        category: suite.integrations.logType || "",
        subcategory: suite.integrations.logLevel || "",
        issueTitle: suite.integrations.issueMessage || "",
        message: suite.integrations.issueDescription || "",
      });
    } else if (intName === "Slack" || intName === "MSTeamsChannel") {
      const originalName = intName === "Slack" ? "Slack" : "MS Teams Channel";
      setWorkItemIds((prev) => ({
        ...prev,
        [originalName]: suite.integrations.logType || "",
      }));
    }

    if (intName === "Jira" || intName === "AzureDevOps" || intName === "ServiceNow") {
      setSelectedGroup("Issue Management");
    } else if (intName === "Slack" || intName === "MSTeamsChannel") {
      setSelectedGroup("Communication");
    } else {
      setSelectedGroup("");
    }
  }, [suite]);

  const isInitialIntegrationLoad = useRef(true);
  useEffect(() => {
    if (isInitialIntegrationLoad.current) {
      isInitialIntegrationLoad.current = false;
      return;
    }

    if (selectedIntegration === "ServiceNow" || selectedIntegration === "Service Now") {
      setIssueobj({ logType: "", logId: "", projectkey: "", message: "" });
    } else if (
      selectedIntegration === "Jira" ||
      selectedIntegration === "Azure Dev Ops" ||
      selectedIntegration === "AzureDevOps"
    ) {
      setServiceNowData({ category: "", subcategory: "", issueTitle: "", message: "" });
    } else if (!selectedIntegration) {
      setIssueobj({ logType: "", logId: "", projectkey: "", message: "" });
      setServiceNowData({ category: "", subcategory: "", issueTitle: "", message: "" });
    }
  }, [selectedIntegration]);

  const generateExceUrl = (integrationType) => {
    const baseURL = `${API_BASE_PATH}${API_BASE_RELATIVE_PATH}?action=DLExecuteSuite&companyid=${temp.companyid}&token=${licenceKey}&prodid=${suite.productid}&SuiteId=${suite.idtest_suite}`;
    return `${baseURL}${integrationType ? `&inttype=${integrationType}` : ""}${
      integrationType === "Azure Dev Ops" && workItemIds["Azure Dev Ops"]
        ? `&WorkItemId=${workItemIds["Azure Dev Ops"]}`
        : ""
    }${integrationType === "Jira" && workItemIds["Jira"] ? `&IssueId=${workItemIds["Jira"]}` : ""}`;
  };

  useEffect(() => {
    const intType = staticIntegrations?.find((item) => item.intname === selectedIntegration);
    setExceUrl(generateExceUrl(intType?.staticintid));
  }, [selectedIntegration, licenceKey, workItemIds]);

  const handleChange = (e) => {
    setIssueobj({ ...issueobj, [e.target.name]: e.target.value });
  };

  const handleSave = () => {
    if (!intType) {
      notify("Please select a valid integration", "warning");
      return;
    }

    const toggleLogLevel = logLevelToggle ? "CaseLevel" : "SuiteLevel";
    const toggleLogType = resultTypeToggle ? "Both" : "OnlyFail";

    const params = {
      tsuid: suite.idtest_suite,
      inttype: intType,
      execLogType: toggleLogType,
      execLogLevel: toggleLogLevel,
      issueDescription: "",
      aioTestCycleId: aioTestCycleId,
    };

    if (selectedIntegration === "ServiceNow" || selectedIntegration === "Service Now") {
      // category → logType, subCategory → logLevel, issueTitle → issueMessage, message → issueDescription
      params.issueLogType = serviceNowData.category;
      params.issueLogLevel = serviceNowData.subcategory;
      params.issueMessage = serviceNowData.issueTitle;
      params.issueDescription = serviceNowData.message;
    } else if (
      selectedIntegration === "Jira" ||
      selectedIntegration === "Azure Dev Ops" ||
      selectedIntegration === "AzureDevOps"
    ) {
      params.issueLogType = issueobj.logType;
      params.logId = issueobj.logId;
      params.projectKey = issueobj.projectkey;
      params.issueDescription = issueobj.message;
    } else if (
      selectedIntegration === "Slack" ||
      selectedIntegration === "MS Teams Channel" ||
      selectedIntegration === "MSTeamsChannel"
    ) {
      params.issueLogType =
        workItemIds[selectedIntegration] ||
        workItemIds["Slack"] ||
        workItemIds["MS Teams Channel"] ||
        "";
    }

    saveIntegrationSettings(params);
  };

  const handleRemove = () => {
    removeIntegration({ tsuid: suite.idtest_suite });
  };

  return (
    <Box py={2}>
      <Box mb={2}>
        <StyledLabel>Integrations</StyledLabel>

        {isLoadingIntegrations ? (
          <Box sx={{ color: "#666", fontSize: "14px", py: 2 }}>Loading integrations...</Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1, flexWrap: "wrap" }}>
            {integrationOptions
              .filter(
                (opt) =>
                  !issueMgmtOptions.some((i) => i.value === opt.value) &&
                  !communicationOptions.some((c) => c.value === opt.value) &&
                  opt.value !== "Github" &&
                  opt.value !== "Impact Analysis",
              )
              .map((option) => (
                <Box
                  key={option.value}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    height: "40px",
                  }}
                >
                  <PillRadio
                    value={option.value}
                    label={option.label}
                    checked={
                      selectedIntegration === option.value ||
                      selectedIntegration === option.value.replace(/\s+/g, "")
                    }
                    onChange={(event) => {
                      const normalizedValue = event.target.value.replace(/\s+/g, "");
                      setSelectedIntegration(normalizedValue);
                      setSelectedGroup(""); // clear group when a pill is selected
                    }}
                  />
                </Box>
              ))}

            {/* Step 1: Integration Type dropdown */}
            <FormControl sx={{ width: "220px" }} size="small">
              <InputLabel id="integration-type-label">Integration Type</InputLabel>
              <Select
                labelId="integration-type-label"
                label="Integration Type"
                value={selectedGroup}
                onChange={(e) => {
                  setSelectedGroup(e.target.value);
                  setSelectedIntegration(""); // reset integration when group changes
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {issueMgmtOptions.length > 0 && (
                  <MenuItem value="Issue Management">Issue Management</MenuItem>
                )}
                {communicationOptions.length > 0 && (
                  <MenuItem value="Communication">Communication</MenuItem>
                )}
              </Select>
            </FormControl>

            {/* Step 2: Integration dropdown — appears only after group is selected */}
            {selectedGroup && (
              <FormControl sx={{ width: "220px" }} size="small">
                <InputLabel id="integration-label">
                  {selectedGroup === "Issue Management" ? "Issue Mgmt Tool" : "Channel"}
                </InputLabel>
                <Select
                  labelId="integration-label"
                  label={selectedGroup === "Issue Management" ? "Issue Mgmt Tool" : "Channel"}
                  value={selectedIntegration}
                  onChange={(e) => setSelectedIntegration(e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {(selectedGroup === "Issue Management"
                    ? issueMgmtOptions
                    : communicationOptions
                  ).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        )}

        {/* Slack / MS Teams URL input — appears below when one is selected */}
        {(selectedIntegration === "Slack" ||
          selectedIntegration === "MS Teams Channel" ||
          selectedIntegration === "MSTeamsChannel") && (
          <Box sx={{ mt: 2, width: "300px" }}>
            <TextField
              fullWidth
              size="small"
              placeholder={selectedIntegration === "Slack" ? "Channel URL" : "Team URL"}
              value={
                workItemIds[
                  selectedIntegration === "MSTeamsChannel"
                    ? "MS Teams Channel"
                    : selectedIntegration
                ] || ""
              }
              onChange={(e) => {
                const key =
                  selectedIntegration === "MSTeamsChannel"
                    ? "MS Teams Channel"
                    : selectedIntegration;
                setWorkItemIds((prev) => ({ ...prev, [key]: e.target.value }));
              }}
            />
          </Box>
        )}
      </Box>

      {selectedIntegration && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            backgroundColor: "#f8f9fa",
          }}
        >
          <Typography sx={{ fontSize: "14px", fontWeight: 600, mb: 2, color: "#555" }}>
            Logging Configuration
          </Typography>

          <Box sx={{ display: "flex", gap: 3, flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 500, minWidth: "100px" }}>
                {logLevelToggle ? "Case Level" : "Suite Level"}
              </Typography>
              <Switch
                size="small"
                checked={logLevelToggle}
                onChange={() => setLogLevelToggle(!logLevelToggle)}
              />
              <Typography sx={{ fontSize: "12px", color: "#666" }}>
                {logLevelToggle ? "Logging at Case Level" : "Logging at Suite Level"}
              </Typography>
            </Box>

            {logLevelToggle && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography sx={{ fontSize: "13px", fontWeight: 500, minWidth: "100px" }}>
                  {resultTypeToggle ? "Both" : "Only Fail"}
                </Typography>
                <Switch
                  size="small"
                  checked={resultTypeToggle}
                  onChange={() => setResultTypeToggle(!resultTypeToggle)}
                />
                <Typography sx={{ fontSize: "12px", color: "#666" }}>
                  {resultTypeToggle ? "Log both Pass and Fail results" : "Log only Failed results"}
                </Typography>
              </Box>
            )}
             <Box>
      <CustomInput
        labelinput="AIO TestCycleId"
        placeholder="Enter AIO Test Cycle ID"
        value={aioTestCycleId}
        onChange={(e) => setAioTestCycleId(e.target.value)}
      />
    </Box>
          </Box>
        </Box>
      )}

      {(selectedIntegration === "Jira" ||
        selectedIntegration === "Azure Dev Ops" ||
        selectedIntegration === "AzureDevOps") && (
        <Box>
          <FormControl sx={{ width: "30%", mr: 1 }}>
            <InputLabel id="log-type-label">Select Log Type</InputLabel>
            <Select
              labelId="log-type-label"
              name="logType"
              value={issueobj.logType}
              onChange={handleChange}
              label="Select Log Type"
              fullWidth
            >
              <MenuItem value="Task">Log Task</MenuItem>
              <MenuItem value="Bug">Log Bug</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ width: "30%", mr: 1 }}>
            <InputLabel id="log-type-logId">Select Issue Type</InputLabel>
            <Select
              labelId="log-type-logId"
              name="logId"
              value={issueobj.logId}
              onChange={handleChange}
              label="Select Issue Type"
              fullWidth
            >
              <MenuItem value="">
                <em>Select Issue Type</em>
              </MenuItem>
              <MenuItem value="new">New Issue-Id</MenuItem>
              <MenuItem value="existing">Provide Issue-ID</MenuItem>
            </Select>
          </FormControl>
          {issueobj.logId &&
            (selectedIntegration === "Azure Dev Ops" ||
              selectedIntegration === "AzureDevOps" ||
              selectedIntegration === "Jira") &&
            issueobj.logId !== "new" && (
              <TextField
                type="text"
                margin="dense"
                name={
                  selectedIntegration === "Azure Dev Ops" || selectedIntegration === "AzureDevOps"
                    ? "WorkItemId"
                    : "IssueId"
                }
                placeholder={
                  selectedIntegration === "Azure Dev Ops" || selectedIntegration === "AzureDevOps"
                    ? "WorkItemId"
                    : "IssueId"
                }
                value={workItemIds[selectedIntegration] || ""}
                onChange={(e) =>
                  setWorkItemIds((prev) => ({
                    ...prev,
                    [selectedIntegration]: e.target.value,
                  }))
                }
                fullWidth
                onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
                onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
              />
            )}
          {issueobj.logId === "new" && (
            <TextField
              margin="dense"
              name="projectkey"
              label="Project Key"
              type="text"
              fullWidth
              value={issueobj.projectkey}
              onChange={handleChange}
            />
          )}
          {issueobj.logId === "new" && (
            <TextField
              margin="dense"
              name="message"
              label="Message"
              type="text"
              fullWidth
              value={issueobj.message}
              onChange={handleChange}
            />
          )}
        </Box>
      )}

      {(selectedIntegration === "ServiceNow" || selectedIntegration === "Service Now") && (
        <Box>
          <FormControl sx={{ width: "30%", mr: 1, mb: 2 }}>
            <InputLabel id="service-now-category">Category</InputLabel>
            <Select
              labelId="service-now-category"
              name="category"
              value={serviceNowData.category}
              onChange={(e) =>
                setServiceNowData((prev) => ({
                  ...prev,
                  category: e.target.value,
                }))
              }
              label="Category"
              fullWidth
            >
              {(serviceNowDropdownConfig?.category || []).map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ width: "30%", mr: 1, mb: 2 }}>
            <InputLabel id="service-now-subcategory">Subcategory</InputLabel>
            <Select
              labelId="service-now-subcategory"
              name="subcategory"
              value={serviceNowData.subcategory}
              onChange={(e) =>
                setServiceNowData((prev) => ({
                  ...prev,
                  subcategory: e.target.value,
                }))
              }
              label="Subcategory"
              fullWidth
            >
              {(serviceNowDropdownConfig?.subcategory || []).map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ width: "30%", mr: 1, mb: 2 }}>
            <InputLabel id="service-now-issue-title">Issue Title</InputLabel>
            <Select
              labelId="service-now-issue-title"
              name="issueTitle"
              value={serviceNowData.issueTitle}
              onChange={(e) =>
                setServiceNowData((prev) => ({
                  ...prev,
                  issueTitle: e.target.value,
                }))
              }
              label="Issue Title"
              fullWidth
            >
              {(serviceNowDropdownConfig?.issueTtitle || []).map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {serviceNowData.category && serviceNowData.subcategory && serviceNowData.issueTitle && (
            <TextField
              margin="dense"
              name="message"
              label="Message"
              type="text"
              fullWidth
              value={serviceNowData.message}
              onChange={(e) =>
                setServiceNowData((prev) => ({
                  ...prev,
                  message: e.target.value,
                }))
              }
              sx={{ mt: 2 }}
            />
          )}
        </Box>
      )}

      {selectedIntegration && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, mb: 2, gap: 2 }}>
          <StyledButton
            variant="outlined"
            color="error"
            sx={{
              height: "45px",
              borderColor: "#d32f2f",
              color: "#d32f2f",
              "&:hover": {
                borderColor: "#b71c1c",
                backgroundColor: "rgba(211, 47, 47, 0.04)",
              },
            }}
            onClick={handleRemove}
          >
            Remove
          </StyledButton>
          <StyledButton
            variant="text"
            color="warning"
            sx={{
              backgroundColor: "#ff5336",
              color: "#ffffff",
              height: "45px",
              boxShadow: "0px 8px 15px 0px rgba(255, 83, 54, 0.2)",
            }}
            onClick={handleSave}
          >
            Save
          </StyledButton>
        </Box>
      )}

      <CustomInput
        labelinput="CI/CD Integration URL"
        placeholder={"Enter Suite Execution URL"}
        value={exceUrl}
      />
    </Box>
  );
});

IntegrationTab.displayName = "IntegrationTab";

export default IntegrationTab;
